import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';
import { Check } from 'lucide-react';
import { cn } from '../../lib/utils';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange'> {
  label?: ReactNode;
  description?: ReactNode;
  onCheckedChange?: (checked: boolean) => void;
  error?: string;
}

const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      className,
      label,
      description,
      onCheckedChange,
      error,
      disabled,
      checked,
      id,
      ...props
    },
    ref
  ) => {
    const inputId = id || props.name;

    return (
      <div className="relative flex items-start">
        <div className="flex h-6 items-center">
          <div className="relative">
            <input
              ref={ref}
              id={inputId}
              type="checkbox"
              disabled={disabled}
              checked={checked}
              onChange={(e) => onCheckedChange?.(e.target.checked)}
              className={cn(
                'peer h-4 w-4 shrink-0 rounded border border-gray-300 dark:border-gray-600',
                'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800',
                'disabled:cursor-not-allowed disabled:opacity-50',
                'checked:bg-primary-600 checked:border-primary-600 dark:checked:bg-primary-500 dark:checked:border-primary-500',
                'appearance-none cursor-pointer',
                error && 'border-red-500 focus:ring-red-500',
                className
              )}
              aria-invalid={error ? 'true' : 'false'}
              aria-describedby={error ? `${inputId}-error` : description ? `${inputId}-description` : undefined}
              {...props}
            />
            <Check
              className={cn(
                'absolute top-0.5 ltr:left-0.5 rtl:right-0.5 h-3 w-3 text-white pointer-events-none',
                'opacity-0 peer-checked:opacity-100 transition-opacity'
              )}
              strokeWidth={3}
            />
          </div>
        </div>
        {(label || description) && (
          <div className="ltr:ml-3 rtl:mr-3 text-sm leading-6">
            {label && (
              <label
                htmlFor={inputId}
                className={cn(
                  'font-medium text-gray-900 dark:text-gray-100',
                  disabled && 'text-gray-500 dark:text-gray-400 cursor-not-allowed'
                )}
              >
                {label}
              </label>
            )}
            {description && (
              <p
                id={`${inputId}-description`}
                className="text-gray-500 dark:text-gray-400"
              >
                {description}
              </p>
            )}
          </div>
        )}
        {error && (
          <p
            id={`${inputId}-error`}
            className="ltr:ml-3 rtl:mr-3 text-sm text-red-600 dark:text-red-400"
            role="alert"
          >
            {error}
          </p>
        )}
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';

export default Checkbox;
